/**
 * Kullanıcılar — the internal user register, owner-only, inside the Ayarlar structure.
 *
 * §6 keeps this function and decides where it lives: «kullanıcı yönetimi gerekiyorsa E-posta ve
 * Doğrulama/Ayarlar yapısı içinde, prototipin kendi diliyle sunulacak». It is therefore NOT a
 * sidebar row of its own — the sidebar is the reference's six entries and nothing else — but a
 * surface of the settings area, offered only to an owner.
 *
 * Owner-only is the authorization matrix's answer, not a convenience: `user.create`, `user.read`,
 * `user.activate`, `user.assign_role` and `user.disable` are owner-ALLOW and admin/operator/viewer-DENY
 * (PO 7.5 §2.1, PO 7.6, AUTHORIZATION-MATRIX v0.7.6). That is also why the settings tab row a
 * reviewer compares against the prototype is unchanged: the prototype has no owner concept and no
 * user management, so there is no reference for this surface, and it appears only where a real
 * owner session exists.
 *
 * The prototype has no user management at all, so the screen itself is derived: reference page
 * head, toolbar, table and modal. Where the register comes from is not this screen's business —
 * it asks the command layer, so the visual acceptance demo answers with its synthetic fixture and
 * the product answers with the owner-only `GET /users`.
 *
 * Five product rules the screen is shaped by, none of them invented here:
 *
 *   - adding a user asks for an address, a name and a role, and nothing else. The auth provider's
 *     subject id is not a field: nobody can type a uuid, and a client that could choose one could
 *     bind a new user to somebody else's provider account — so the server looks the address up,
 *     invites it once if the provider has no account for it, and binds the row to that account;
 *   - one active role per user (PO 7.2 §11) — there is no union of permissions, so the table has
 *     a single «Rol» column rather than a list, and a user with no role is denied everything;
 *   - activation is an explicit owner act (PO 7.6) whose only permitted transition is
 *     `invited → active`: a suspended user stays suspended, and the control says so instead of
 *     silently reactivating;
 *   - a role change is ONE transaction on the server (PO 7.2 §11.2): the old assignment is
 *     revoked and the new one opened, `role_revoked` + `role_assigned` under one correlation id.
 *     The screen neither merges roles nor keeps the old one beside the new;
 *   - disabling suspends the account and revokes whatever role it held. It is not a delete, and
 *     `suspended → active` is a separate, undecided question with no command at all.
 *
 * Three refusals belong to the backend and are never anticipated here: the last-owner guard (the
 * database trigger), `role_unchanged`, and `user_not_found`. What the server says is what the
 * dialog shows, with the form still filled in, because a refusal the operator has to retype is a
 * refusal they will work around.
 *
 * Neither answer carries a secret-class field: no password, no MFA secret, no session token, no
 * auth-provider subject id. MFA and e-mail verification are booleans, exactly as `user.read`
 * returns them.
 */
import { type ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useV07Commands } from '../data/commands-context';
import { fmtDateTime } from '../lib/reference';
import { DerivedNotice } from '../ui/DerivedNotice';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';

type Role = 'owner' | 'admin' | 'operator' | 'viewer';

interface DemoUser {
  id: string;
  email: string;
  displayName: string;
  status: 'invited' | 'active' | 'suspended';
  activeRole: Role | null;
  roleGrantedAt: string | null;
  emailVerified: boolean;
  mfaEnrolled: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const STATUS_TR: Record<DemoUser['status'], string> = {
  invited: 'Davet edildi',
  active: 'Aktif',
  suspended: 'Askıya alındı',
};

const STATUS_PILL: Record<DemoUser['status'], string> = {
  invited: 'pill-info',
  active: 'pill-active',
  suspended: 'pill-revoked',
};

const ROLE_TR: Record<Role, string> = {
  owner: 'Sahip',
  admin: 'Yönetici',
  operator: 'Operatör',
  viewer: 'Görüntüleyici',
};

const ROLES: Role[] = ['owner', 'admin', 'operator', 'viewer'];

/** The server's own shape rule for a reason (`minLength: 3`), mirrored so the form can say it. */
const MIN_REASON = 3;

type Action = 'activate' | 'role' | 'disable';

interface Pending {
  user: DemoUser;
  action: Action;
}

const TITLE: Record<Action, string> = {
  activate: 'Kullanıcıyı aktif et',
  role: 'Rolü değiştir',
  disable: 'Hesabı devre dışı bırak',
};

export function SettingsUsersScreen(): ReactElement {
  const commands = useV07Commands();
  const [users, setUsers] = useState<DemoUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [confirming, setConfirming] = useState<Pending | null>(null);
  /*
   * The dialog's own state. It is deliberately NOT reset when a command fails: the reason somebody
   * typed and the role they chose are the expensive part of the act, and a refusal that empties the
   * form teaches them to retry blind.
   */
  const [reason, setReason] = useState('');
  const [targetRole, setTargetRole] = useState<Role>('viewer');
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /*
   * «Kullanıcı ekle». Its own dialog, and its own state for the same reason the one above keeps
   * its: a refusal that empties the form teaches somebody to retry blind. There is no field for the
   * auth provider's subject id — nobody can type a uuid, and the server resolves it from the
   * address — so what is asked for here is exactly what a person knows.
   */
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('owner');
  const [addError, setAddError] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    const r = await commands.listUsers();
    setUsers(r.users as DemoUser[]);
    setLoadError(r.ok ? null : (r.message ?? 'Kullanıcılar okunamadı'));
    setLoading(false);
  }, [commands]);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const r = await commands.listUsers();
      if (!alive) return;
      setUsers(r.users as DemoUser[]);
      setLoadError(r.ok ? null : (r.message ?? 'Kullanıcılar okunamadı'));
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [commands]);

  const rows = useMemo(() => {
    const q = query.trim().toLocaleLowerCase('tr');
    return users.filter((u) => {
      if (status && u.status !== status) return false;
      if (!q) return true;
      return [u.displayName, u.email, u.activeRole ?? ''].some((v) => v.toLocaleLowerCase('tr').includes(q));
    });
  }, [users, query, status]);

  function open(user: DemoUser, action: Action): void {
    setConfirming({ user, action });
    setReason('');
    setFormError(null);
    setBusy(false);
    /* Start on a role the user does not already hold, so the first thing offered is a real change. */
    setTargetRole(ROLES.find((r) => r !== user.activeRole) ?? 'viewer');
  }

  function close(): void {
    if (busy) return;
    setConfirming(null);
    setFormError(null);
  }

  function openAdd(): void {
    setAdding(true);
    setNewName('');
    setNewEmail('');
    setNewRole('owner');
    setAddError(null);
    setBusy(false);
  }

  function closeAdd(): void {
    if (busy) return;
    setAdding(false);
    setAddError(null);
  }

  /** The shape rules this form can state for itself; the backend schema is still the gate. */
  function addIssue(name: string, email: string): string | null {
    if (name.length === 0) return 'Ad soyad gerekli.';
    if (email.length === 0) return 'E-posta adresi gerekli.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Geçerli bir e-posta adresi girin.';
    if (!ROLES.includes(newRole)) return 'Geçerli bir rol seçin.';
    return null;
  }

  async function submitNew(): Promise<void> {
    const name = newName.trim();
    const email = newEmail.trim();
    const issue = addIssue(name, email);
    if (issue !== null) {
      setAddError(issue);
      return;
    }
    setAddError(null);
    setBusy(true);
    try {
      const r = await commands.createUser({ email, displayName: name, role: newRole });
      if (!r.ok) {
        /* What the server said, with the form still filled in. */
        setAddError(r.message ?? 'Kullanıcı oluşturulamadı');
        return;
      }
      setAdding(false);
      /* The register is re-read rather than patched: the row the table shows is the server's. */
      await load();
      toast(
        r.providerAccount === 'existing'
          ? `${name} eklendi · sağlayıcıda hesabı zaten vardı · durumu «Davet edildi»`
          : `${name} eklendi · davet gönderildi · durumu «Davet edildi»`,
        'ok',
      );
    } finally {
      setBusy(false);
    }
  }

  /** Replaces one row with what the SERVER said it now is — never with what the click intended. */
  function applyServerState(userId: string, patch: Partial<DemoUser>): void {
    setUsers((list) => list.map((u) => (u.id === userId ? { ...u, ...patch } : u)));
  }

  async function submit(pending: Pending): Promise<void> {
    const { user, action } = pending;
    setFormError(null);

    /*
     * PO 7.6: the only permitted transition is invited → active. The backend refuses the other two
     * with 409s of its own, and the screen states the same refusal without a round trip rather
     * than hiding a control the owner can legitimately see.
     */
    if (action === 'activate' && user.status === 'active') {
      setFormError(`${user.displayName} zaten aktif (409 user_already_active)`);
      return;
    }
    if (action === 'activate' && user.status === 'suspended') {
      setFormError(`${user.displayName} askıda; sessiz reaktivasyon yok (409 user_suspended)`);
      return;
    }
    const written = reason.trim();
    if (written.length < MIN_REASON) {
      setFormError(`Gerekçe en az ${MIN_REASON} karakter olmalı; denetim defterine bu metin yazılır.`);
      return;
    }

    setBusy(true);
    try {
      if (action === 'activate') {
        const r = await commands.activateUser({ userId: user.id, reason: written });
        if (!r.ok) {
          setFormError(r.message ?? 'Aktif edilemedi');
          return;
        }
        applyServerState(user.id, { status: 'active' });
        setConfirming(null);
        toast(`${user.displayName} aktif edildi · denetim defterine user_activated yazıldı`, 'ok');
        return;
      }
      if (action === 'role') {
        const r = await commands.assignRole({ userId: user.id, role: targetRole, reason: written });
        if (!r.ok) {
          setFormError(r.message ?? 'Rol değiştirilemedi');
          return;
        }
        /* `message` is the role the SERVER opened the assignment for; the table follows that. */
        const granted = ROLES.find((role) => role === r.message) ?? targetRole;
        applyServerState(user.id, { activeRole: granted });
        setConfirming(null);
        toast(`${user.displayName} artık ${ROLE_TR[granted]} · role_revoked + role_assigned yazıldı`, 'ok');
        return;
      }
      const r = await commands.disableUser({ userId: user.id, reason: written });
      if (!r.ok) {
        setFormError(r.message ?? 'Hesap devre dışı bırakılamadı');
        return;
      }
      /* Disabling revokes the active role as well; the row says so rather than keeping a dead pill. */
      applyServerState(user.id, { status: 'suspended', activeRole: null, roleGrantedAt: null });
      setConfirming(null);
      toast(`${user.displayName} devre dışı bırakıldı · rolü kaldırıldı`, 'ok');
    } finally {
      setBusy(false);
    }
  }

  const sameRole = confirming?.action === 'role' && confirming.user.activeRole === targetRole;

  return (
    <section data-screen="users">
      <DerivedNotice note="Yalnız sahip rolünün görebildiği kullanıcı yönetimi; backend'de GET /users, POST /users, POST /users/:id/activate, POST /users/:id/role ve POST /users/:id/disable karşılığı vardır. Buradaki kullanıcılar sentetiktir." />

      <div className="page-head">
        <div>
          <span className="eyebrow">Erişim yönetimi</span>
          <h1>Kullanıcılar</h1>
          <p className="lead">
            {'Her aktif kullanıcının '}
            <b>tam olarak bir</b>
            {
              ' aktif rolü vardır; roller birleşmez ve rolü olmayan kullanıcı hiçbir şeye yetkili değildir. Bu ekranı yalnız sahip rolü görür.'
            }
          </p>
        </div>
      </div>

      {loadError !== null ? (
        <div className="warn-box" role="alert">
          {loadError}
        </div>
      ) : null}

      <div className="toolbar">
        <input
          className="input search"
          placeholder="Ad, e-posta veya rol ara…"
          autoComplete="off"
          aria-label="Ara"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <select
          className="select"
          aria-label="Durum"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">Tüm durumlar</option>
          <option value="invited">Davet edildi</option>
          <option value="active">Aktif</option>
          <option value="suspended">Askıya alındı</option>
        </select>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          id="usrAdd"
          data-act="add-user"
          disabled={busy}
          onClick={openAdd}
        >
          Kullanıcı ekle
        </button>
        <button
          className="btn btn-secondary btn-sm"
          type="button"
          data-act="reload"
          disabled={loading}
          onClick={() => void load()}
        >
          {loading ? 'Okunuyor…' : 'Yenile'}
        </button>
        <span className="muted small" data-users-count>
          {loading ? 'Kullanıcılar okunuyor…' : `${rows.length} kullanıcı`}
        </span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Kullanıcı</th>
                <th>Rol</th>
                <th>Durum</th>
                <th>E-posta doğrulandı</th>
                <th>MFA</th>
                <th>Son giriş</th>
                <th scope="col">
                  <span className="sr-only">İşlemler</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u) => (
                <tr key={u.id} data-user={u.id}>
                  <td>
                    <b style={{ fontWeight: 500 }}>{u.displayName}</b>
                    <span className="small muted" style={{ display: 'block' }}>
                      {u.email}
                    </span>
                  </td>
                  <td data-cell="role">
                    {u.activeRole ? (
                      <span className="pill pill-neutral">{ROLE_TR[u.activeRole]}</span>
                    ) : (
                      <span className="muted small">Rol yok</span>
                    )}
                  </td>
                  <td data-cell="status">
                    <span className={`pill ${STATUS_PILL[u.status]}`}>{STATUS_TR[u.status]}</span>
                  </td>
                  <td className="small">{u.emailVerified ? 'Evet' : 'Hayır'}</td>
                  <td className="small">{u.mfaEnrolled ? 'Kayıtlı' : 'Kayıtsız'}</td>
                  <td className="small tnum nowrap">
                    {u.lastLoginAt ? fmtDateTime(u.lastLoginAt, 'tr') : '—'}
                  </td>
                  <td>
                    <div className="actions" data-hpw-user-actions>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        data-act="activate"
                        disabled={u.status !== 'invited'}
                        onClick={() => open(u, 'activate')}
                      >
                        Aktif et
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        data-act="role"
                        disabled={u.status === 'suspended'}
                        onClick={() => open(u, 'role')}
                      >
                        Rolü değiştir
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        type="button"
                        data-act="disable"
                        disabled={u.status === 'suspended'}
                        onClick={() => open(u, 'disable')}
                      >
                        Devre dışı bırak
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 ? (
          <div className="card-pad">
            <p className="muted small">
              {loading ? 'Kullanıcılar okunuyor…' : 'Bu filtreye uyan kullanıcı yok.'}
            </p>
          </div>
        ) : null}
      </div>

      {adding ? (
        <Modal label="Kullanıcı ekle" onClose={closeAdd}>
          <ModalHead title="Kullanıcı ekle" onClose={closeAdd} />
          <div className="card-pad stack">
            <div className="field">
              <label htmlFor="newUserName">Ad Soyad</label>
              <input
                id="newUserName"
                className="input"
                type="text"
                autoComplete="off"
                value={newName}
                disabled={busy}
                onChange={(e) => setNewName(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="newUserEmail">E-posta</label>
              <input
                id="newUserEmail"
                className="input"
                type="email"
                autoComplete="off"
                value={newEmail}
                disabled={busy}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="newUserRole">Rol</label>
              <select
                id="newUserRole"
                className="select"
                value={newRole}
                disabled={busy}
                onChange={(e) => setNewRole(e.target.value as Role)}
              >
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_TR[r]}
                  </option>
                ))}
              </select>
            </div>
            <p className="help">
              {
                'Kullanıcı «Davet edildi» durumunda oluşturulur ve bu komut e-posta doğrulamasını ya da ikinci faktörü kendi başına işaretlemez. Giriş hesabı adresten bulunur; sağlayıcıda yoksa bu adrese bir kez davet gönderilir. Aktif etmek ayrı bir işlemdir.'
              }
            </p>

            {addError === null ? null : (
              <div className="warn-box" role="alert" data-user-error="add">
                {addError}
              </div>
            )}
          </div>
          <div className="card-foot">
            <button
              className="btn btn-secondary"
              type="button"
              data-close="1"
              disabled={busy}
              onClick={closeAdd}
            >
              Vazgeç
            </button>
            <button
              className="btn btn-primary"
              type="button"
              id="usrAddConfirm"
              data-act="confirm-add"
              disabled={busy}
              onClick={() => void submitNew()}
            >
              {busy ? 'Gönderiliyor…' : 'Kullanıcı ekle'}
            </button>
          </div>
        </Modal>
      ) : null}

      {confirming === null ? null : (
        <Modal label={TITLE[confirming.action]} onClose={close}>
          <ModalHead title={TITLE[confirming.action]} onClose={close} />
          <div className="card-pad stack">
            <p>
              <b>{confirming.user.displayName}</b>
              {` (${confirming.user.email})`}
            </p>

            {confirming.action === 'activate' ? (
              <>
                <p>{'«Davet edildi» durumundan «aktif» durumuna geçirilecek.'}</p>
                <p className="help">
                  {
                    'Bu komut rol atamaz ve e-posta doğrulamasını kendi başına işaretlemez. Geçiş, kalıcı denetim defterine user_activated olayı olarak yazılır; olay yazılamazsa aktivasyon geri alınır.'
                  }
                </p>
              </>
            ) : null}

            {confirming.action === 'role' ? (
              <>
                <div className="field-row">
                  <div className="field">
                    <span className="label">Mevcut rol</span>
                    <p data-field="current-role">
                      {confirming.user.activeRole ? (
                        <span className="pill pill-neutral">{ROLE_TR[confirming.user.activeRole]}</span>
                      ) : (
                        <span className="muted small">Rol yok</span>
                      )}
                    </p>
                  </div>
                  <div className="field">
                    <label htmlFor="userTargetRole">Yeni rol</label>
                    <select
                      id="userTargetRole"
                      className="select"
                      value={targetRole}
                      disabled={busy}
                      onChange={(e) => setTargetRole(e.target.value as Role)}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {r === confirming.user.activeRole ? `${ROLE_TR[r]} (mevcut rol)` : ROLE_TR[r]}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <p className="help">
                  {
                    'Roller birleşmez: mevcut rol tek işlemde kaldırılır ve yenisi verilir; denetim defterine role_revoked ve role_assigned olarak yazılır. Son sahibin rolünü düşürme ve kullanıcının hâlihazırda sahip olduğu rolü yeniden verme sunucu tarafından reddedilir.'
                  }
                </p>
              </>
            ) : null}

            {confirming.action === 'disable' ? (
              <>
                <p>
                  {
                    'Hesap «askıya alındı» durumuna geçirilecek ve varsa aktif rolü kaldırılacak. Kullanıcı bundan sonra hiçbir korumalı isteği yapamaz.'
                  }
                </p>
                <p className="help">
                  {
                    'Bu bir silme değildir: kullanıcı kaydı, geçmişi ve ürettiği belgeler durur. Askıya alınmış bir hesabı geri açan komut yoktur; bu ayrı bir karardır.'
                  }
                </p>
              </>
            ) : null}

            <div className="field">
              <label htmlFor="userReason">Gerekçe</label>
              <textarea
                id="userReason"
                className="textarea"
                rows={2}
                value={reason}
                disabled={busy}
                placeholder="Bu işlemin nedeni"
                onChange={(e) => setReason(e.target.value)}
              />
              <span className="help">
                {'Bu metin denetim defterine olduğu gibi yazılır; en az '}
                {MIN_REASON}
                {' karakter.'}
              </span>
            </div>

            {formError === null ? null : (
              <div className="warn-box" role="alert" data-user-error>
                {formError}
              </div>
            )}
          </div>
          <div className="card-foot">
            <button
              className="btn btn-secondary"
              type="button"
              data-close="1"
              disabled={busy}
              onClick={close}
            >
              Vazgeç
            </button>
            <button
              className="btn btn-primary"
              type="button"
              data-act="confirm"
              disabled={busy || sameRole}
              onClick={() => void submit(confirming)}
            >
              {busy ? 'Gönderiliyor…' : TITLE[confirming.action]}
            </button>
          </div>
          {sameRole ? (
            <div className="card-pad">
              <p className="help" data-hint="same-role">
                {'Kullanıcı zaten bu role sahip; değişiklik için farklı bir rol seçin.'}
              </p>
            </div>
          ) : null}
        </Modal>
      )}
    </section>
  );
}
